<template>
    <div>
        <div v-if="$loadingRouteData"
             class="container -producer">
            <loader></loader>
        </div>

        <div v-if="!$loadingRouteData"
             class="container -sheet">
            <ol class="breadcrumb">
                <li><a v-link="{name: 'home'}">Accueil</a></li>
                <li><a v-link="{name: 'sheet-list'}">Bordereaux</a></li>
                <li>Saisie</li>
            </ol>

            <div class="heading">
                <h1 class="f -a-center -gutter-small">
                    <div class="expand">Saisie d'un borderau</div>

                    <div>
                        <a v-link="{name: 'sheet-list'}"
                           class="btn btn-link">
                            Annuler
                        </a>
                        <a class="btn btn-primary -save-sheet"
                           v-if="!sheet.frozen"
                           @click.prevent="save">
                            <loader :inline="true" v-if="loading"></loader>
                            Sauvegarder
                        </a>
                    </div>
                </h1>
                <h2 v-if="!sheet.frozen">
                    <editable-date :value.sync="sheet.date"></editable-date>
                    &mdash;
                    <editable-text :value.sync="sheet.reference"
                                   placeholder="Référence"></editable-text>
                </h2>
                <h2 v-if="sheet.frozen">
                    {{ sheet.date | date 'LL' }}
                    &mdash;
                    {{ sheet.reference }}
                </h2>
            </div>

            <table class="table table-striped"
                   v-drop-zone="validateXls">
                <tr>
                    <th>Contrat</th>
                    <th class="prime">Acquisition</th>
                    <th class="prime">Encours</th>
                    <th class="prime">Reprise</th>
                </tr>

                <tr v-for="(idx, i) of sheet.interests"
                    class="interest"
                    :class="{'-pristine': isPristine(idx)}">
                    <td>
                        #{{ i.contract.id }}
                        <span v-if="i.contract.number">
                            &ndash; {{ i.contract.number }}
                        </span>
                        &ndash;
                        {{ i.contract.client.name }}
                    </td>
                    <td class="number"
                        v-if="!sheet.frozen">
                        <editable-number :value.sync="i.pu"
                                         v-bind:class="'interests-pu-' + i.contract.id">
                        </editable-number>
                    </td>
                    <td class="number"
                        v-if="!sheet.frozen">
                        <editable-number :value.sync="i.pp"
                                         v-bind:class="'interests-pp-' + i.contract.id">
                        </editable-number>
                    </td>
                    <td class="number"
                        v-if="!sheet.frozen">
                        <editable-number :value.sync="i.reprise"
                                         v-bind:class="'interests-reprise-' + i.contract.id">
                        </editable-number>
                    </td>
                    <td class="number"
                        v-if="sheet.frozen">
                        {{ i.pu }}
                    </td>
                    <td class="number"
                        v-if="sheet.frozen">
                        {{ i.pp }}
                    </td>
                    <td class="number"
                        v-if="sheet.frozen">
                        {{ i.reprise }}
                    </td>
                </tr>

                <tr class="total">
                    <td rowspan="2">Total</td>
                    <td class="number -pu">{{{ totalPu | money }}}</td>
                    <td class="number -pp">{{{ totalPp | money }}}</td>
                    <td class="number -reprise">{{{ totalReprise | money }}}</td>
                </tr>

                <tr class="total">
                    <td colspan="3" class="number -overall">{{{ total | money }}}</td>
                </tr>
            </table>
        </div>
    </div>
</template>

<style scoped>
    .total {
        font-weight: bold;
    }

    .number {
        text-align: right;
    }

    .prime {
        width: 200px;
        text-align: right;
    }

    .interest {
        color: grey;
    }

    .-pristine {
        color: black;
    }
</style>

<script type="text/ecmascript-6">
    import xlsx from 'xlsx';
    import Loader from '../components/Loader';
    import EditableText from '../components/editable/EditableText';
    import EditableDate from '../components/editable/EditableDate';
    import EditableNumber from '../components/editable/EditableNumber';
    import {emptySheet, create, update, fetch} from '../api/sheets';
    import router from '../router';
    import {exception as se} from '../sink';
    import {d, asyncAction, items} from '../utils';
    import {manager as msgManager} from '../messages';
    import config from '../config';
    import dropZone from '../directives/drop-zone';

    const COLS_MAPPING = {
        contrat: 'contract_id',
        acquisition: 'pu',
        encours: 'pp',
        reprise: 'reprise'
    };

    export default {
        components: {
            Loader,
            EditableNumber,
            EditableDate,
            EditableText
        },
        directives: {
            dropZone
        },
        data() {
            return {
                sheet: {},
                pristineMap: {},
                loading: false
            };
        },
        computed: {
            totalPu() {
                return this.totalX('pu');
            },
            totalPp() {
                return this.totalX('pp');
            },
            totalReprise() {
                return this.totalX('reprise');
            },
            total() {
                return this.totalPu + this.totalPp - this.totalReprise;
            }
        },
        route: {
            data: async function (transition) {
                try {
                    const toId = d(() => transition.to.params.id);

                    if (d(() => transition.to.name) === 'sheet-edit' &&
                            toId === d(() => this.sheet.id)) {
                        return;
                    }

                    if (!toId) {
                        return {
                            sheet: await emptySheet(transition.to.params.company)
                        };
                    } else {
                        return {
                            sheet: this.invertReprise(await fetch(toId))
                        };
                    }
                } catch (e) {
                    se(e);
                    router.replace({name: 'not-found'});
                }
            }
        },
        created() {
            this.$on('file-drop', this.handleXls);
        },
        methods: {
            /**
             * Check that the files being dropped are actually a spreadsheet
             */
            validateXls(dt) {
                const allowed = config.ALLOWED_SPREADSHEET_TYPES;
                const allowedExp =
                    new RegExp(`\.(${Object.keys(allowed).join('|')})$`, 'i');

                if (!Array.from(dt.types).some((t) => t === 'Files')) {
                    return false;
                }

                if (Array
                        .from(dt.files || [])
                        .some((f) => !allowedExp.test(f.name))
                ) {
                    const a = Array
                        .from(items(allowed))
                        .map(([, v]) => v)
                        .join(', ');

                    msgManager.push(
                            'Attention.',
                            `Type de fichier non autorisé. Attendu: ${a}`,
                            'warning'
                    );

                    return false;
                }

                return true;
            },

            /**
             * When a XLS file is uploaded, handle its content and patch the
             * current table
             */
            handleXls(file) {
                const rdr = new FileReader();

                rdr.onloadend = () => {
                    const wb = xlsx.read(rdr.result, {type: 'binary'});
                    const ws = wb.Sheets[wb.SheetNames[0]];
                    const json = xlsx.utils.sheet_to_json(ws, {raw: true});
                    this.patch(json);
                };

                rdr.readAsBinaryString(file);
            },

            /**
             * Normalize text to help matching
             */
            normalize(text) {
                if (!text) {
                    return '';
                }

                return text.toString().toLowerCase();
            },

            /**
             * Transforms the raw input sheet into an indexed object
             */
            makePatch(input) {
                const out = {};

                for (const rawRow of input) {
                    const row = {};
                    let contractId = null;

                    for (const [rawKey, value] of items(rawRow)) {
                        const key = COLS_MAPPING[this.normalize(rawKey)];

                        if (key === 'contract_id') {
                            contractId = this.normalize(value);
                        } else if (key && typeof value === 'number') {
                            row[key] = value;
                        }
                    }

                    if (contractId) {
                        if (!Object
                                .prototype
                                .hasOwnProperty
                                .call(out, contractId)) {
                            out[contractId] = row;
                        } else {
                            for (const [key, value] of items(row)) {
                                out[contractId][key] =
                                    value + (out[contractId][key] || 0);
                            }
                        }
                    }
                }

                return out;
            },

            /**
             * Patch the current sheet with some input data (that probably
             * comes from a dropped XLS file)
             */
            patch(input) {
                const patch = this.makePatch(input);
                let patched = 0;

                for (const row of (this.sheet.interests || [])) {
                    const key = this.normalize(row.contract.number);

                    if (Object.prototype.hasOwnProperty.call(patch, key)) {
                        const rowPatch = patch[key];

                        for (const [key, value] of items(rowPatch)) {
                            row[key] += value;
                        }

                        patched += 1;
                    }
                }

                msgManager.push(
                    'Succès.',
                    `${patched}/${input.length} lignes importées`,
                    'success'
                );
            },

            /**
             * Check if a given row has at least one non-null value
             */
            isNull(rowIdx) {
                const keys = ['pu', 'pp', 'reprise'];
                return !keys.some((k) => this.sheet.interests[rowIdx][k]);
            },

            /**
             * Checks if a given row is pristine or not (this function updates
             * an internal pristine cache, there is no watch per se on the
             * values, which means that you need to call this method
             * reactively for it to work).
             */
            isPristine(rowIdx) {
                if (!this.isNull(rowIdx)) {
                    this.pristineMap[rowIdx] = true;
                }

                if (this.pristineMap[rowIdx]) {
                    return false;
                }

                return true;
            },

            invertReprise(sheet) {
                return Object.assign({}, sheet, {
                    interests: sheet.interests.map((i) => Object.assign({}, i, {
                        reprise: -(i.reprise || 0)
                    }))
                });
            },
            totalX(x) {
                return d(() => this.sheet.interests, [])
                    .map((i) => i[x])
                    .reduce((a, b) => a + b, 0);
            },
            save: asyncAction('loading', async function () {
                if (!this.sheet.id) {
                    this.sheet = this.invertReprise(await create(this.invertReprise(this.sheet)));
                    router.replace({name: 'sheet-edit', params: {id: this.sheet.id}});
                } else {
                    this.sheet = this.invertReprise(await update(this.invertReprise(this.sheet)));
                }

                msgManager.push(
                    'Succès.',
                    'Le bordereau a bien été enregistré.',
                    'success'
                );
            })
        }
    };
</script>



// WEBPACK FOOTER //
// Sheet.vue?e3aac758